
import { useEffect, useState } from "react";
import { PriceCard } from "@/components/PriceCard";
import { useStore } from "@/store/useStore";
import { toast } from "sonner";
import { MetalType, useMetalSelection } from "@/hooks/useMetalSelection";
import { RefreshButton } from "./market-prices/RefreshButton";
import { PriceErrorDisplay } from "./market-prices/PriceErrorDisplay";
import { PriceCardsContainer } from "./market-prices/PriceCardsContainer";
import { MetalCardWrapper } from "./market-prices/MetalCardWrapper";
import { formatPrice, calculateGoldPrice, calculateSilverPrice, Unit, GoldPurity, SilverPurity } from "@/lib/pricing";

interface MarketPricesProps {
  onMetalChange?: (metal: MetalType) => void;
}

export function MarketPrices({ onMetalChange }: MarketPricesProps) {
  const { 
    currentGoldPrice,
    currentSilverPrice,
    goldPurity,
    silverPurity,
    silverUnit,
    isPriceLoading,
    error,
    setGoldPurity,
    setSilverPurity,
    setSilverUnit,
    fetchCurrentPricesData,
  } = useStore();

  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!currentGoldPrice && !currentSilverPrice) {
      fetchCurrentPricesData();
    }
  }, [fetchCurrentPricesData, currentGoldPrice, currentSilverPrice]);
  
  const { activeMetal, handleMetalClick } = useMetalSelection({ onMetalChange });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchCurrentPricesData();
      toast.success("Prezzi aggiornati con successo");
    } catch (err) {
      console.error("Errore durante l'aggiornamento dei prezzi:", err);
      toast.error("Impossibile aggiornare i prezzi. Riprova più tardi.");
    } finally {
      setIsRefreshing(false);
    }
  };

  if (error && !currentGoldPrice && !currentSilverPrice) {
    return (
      <div className="w-full">
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-3xl font-bold metal-text tracking-tight">Prezzi di Mercato</h2>
          <RefreshButton isLoading={isRefreshing} onRefresh={handleRefresh} />
        </div>
        <PriceErrorDisplay />
      </div>
    );
  }

  const goldDisplayPrice = currentGoldPrice ? calculateGoldPrice(currentGoldPrice, goldPurity) : 0;
  const silverDisplayPrice = currentSilverPrice ? calculateSilverPrice(currentSilverPrice, silverPurity, silverUnit) : 0;

  return (
    <div className="w-full">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-3xl font-bold metal-text tracking-tight">Prezzi di Mercato</h2>
        <RefreshButton isLoading={isRefreshing || isPriceLoading} onRefresh={handleRefresh} />
      </div>
      
      <PriceCardsContainer>
        <MetalCardWrapper 
          metal="gold" 
          activeMetal={activeMetal} 
          onSelect={() => handleMetalClick("gold")}
        >
          <PriceCard 
            metalPrice={currentGoldPrice}
            isLoading={isPriceLoading && !currentGoldPrice}
            metal="gold"
            purity={goldPurity}
            formattedPrice={formatPrice(goldDisplayPrice, 'g')}
            onPurityChange={(p) => setGoldPurity(p as GoldPurity)}
          />
        </MetalCardWrapper>
        
        <MetalCardWrapper 
          metal="silver" 
          activeMetal={activeMetal} 
          onSelect={() => handleMetalClick("silver")}
        >
          <PriceCard 
            metalPrice={currentSilverPrice}
            isLoading={isPriceLoading && !currentSilverPrice}
            metal="silver"
            purity={silverPurity}
            unit={silverUnit}
            formattedPrice={formatPrice(silverDisplayPrice, silverUnit)}
            onPurityChange={(p) => setSilverPurity(p as SilverPurity)}
            onUnitChange={(u) => setSilverUnit(u as Unit)}
          />
        </MetalCardWrapper>
      </PriceCardsContainer>
    </div>
  );
}
